import {
  AbstractPowerSyncDatabase,
  PowerSyncBackendConnector,
  PowerSyncCredentials,
  UpdateType,
} from '@powersync/common';
import { authVar } from '@/lib/apollo/auth';
import { apolloClient } from '@/lib/apollo';
import { env } from '@/lib/env';
import { logger } from '@/lib/logger';
import { refreshToken } from '@/lib/auth/refresh-token';
import {
  ExecutePowerSyncBatchDocument,
  type PowerSyncDataOperation,
  PowerSyncOperation,
} from '~/graphql/generated/graphql';

const OPERATION_TYPE_MAPPING = {
  [UpdateType.PUT]: PowerSyncOperation.Put,
  [UpdateType.PATCH]: PowerSyncOperation.Patch,
  [UpdateType.DELETE]: PowerSyncOperation.Delete,
} as const;

export class ApolloPowerSyncConnector implements PowerSyncBackendConnector {
  async fetchCredentials(): Promise<null | PowerSyncCredentials> {
    const auth = authVar();
    if (!auth) {
      logger.debug('PowerSync fetchCredentials: no auth available');
      return null;
    }

    // Check if token is expired or expiring soon (within 5 minutes)
    const expiresAtMs = (auth.issuedAt + auth.expiresIn) * 1000;
    const timeUntilExpiry = expiresAtMs - Date.now();
    const isExpiringSoon = timeUntilExpiry < 5 * 60 * 1000;

    logger.debug('PowerSync fetchCredentials - auth state:', {
      hasAccessToken: !!auth.accessToken,
      expiresIn: auth.expiresIn,
      issuedAt: auth.issuedAt,
      expiresAt: new Date(expiresAtMs).toISOString(),
      timeUntilExpiryMinutes: Math.round(timeUntilExpiry / 1000 / 60),
      isExpiringSoon,
    });

    // Refresh token if expired or expiring soon
    if (isExpiringSoon && auth.refreshToken) {
      try {
        logger.debug(
          'Token expiring soon, refreshing before providing credentials',
        );
        await refreshToken(auth.refreshToken);
        // refreshToken updates authVar internally, read the new value
        const refreshedAuth = authVar();
        if (!refreshedAuth) {
          logger.error('Auth cleared during refresh');
          return null;
        }
        // No connection params: the history window is enforced server-side via the
        // trusted `history_from` access-token claim (Zitadel complement-token action),
        // so nothing client-supplied gates the sync.
        return {
          endpoint: env.powersyncUrl.toString().replace(/\/$/, ''),
          token: refreshedAuth.accessToken,
          expiresAt: new Date(
            (refreshedAuth.issuedAt + refreshedAuth.expiresIn) * 1000,
          ),
        };
      } catch (error) {
        logger.error('Failed to refresh token in PowerSync connector:', error);
        return null;
      }
    }

    // No connection params: the history window is enforced server-side via the trusted
    // `history_from` access-token claim (Zitadel complement-token action).
    const credentials = {
      endpoint: env.powersyncUrl.toString().replace(/\/$/, ''),
      token: auth.accessToken,
      expiresAt: new Date((auth.issuedAt + auth.expiresIn) * 1000),
    };

    logger.debug('PowerSync credentials prepared:', {
      endpoint: credentials.endpoint,
      tokenLength: credentials.token?.length,
      expiresAt: credentials.expiresAt.toISOString(),
    });

    return credentials;
  }

  async uploadData(database: AbstractPowerSyncDatabase) {
    /**
     * For batched crud transactions, use data.getCrudBatch(n);
     * https://powersync-ja.github.io/powersync-js/react-native-sdk/classes/SqliteBucketStorage#getcrudbatch
     */
    const transaction = await database.getNextCrudTransaction();

    if (!transaction) {
      return;
    }

    const operations: PowerSyncDataOperation[] = transaction.crud
      .map((op) => {
        const powerSyncOp = OPERATION_TYPE_MAPPING[op.op];
        if (!powerSyncOp) {
          logger.warn(`Unknown operation type: ${op.op}`);
          return null;
        }

        // The data that needs to be changed in the remote db
        const record = { ...op.opData, id: op.id };

        return {
          op: powerSyncOp,
          table: op.table,
          data: record,
          id: op.id,
        };
      })
      .filter((op): op is PowerSyncDataOperation => op !== null);

    if (operations.length > 0) {
      // Execute the batch operation via GraphQL
      const result = await apolloClient.mutate({
        mutation: ExecutePowerSyncBatchDocument,
        variables: {
          input: {
            batch: operations,
          },
        },
      });

      if (!result.data?.executePowerSyncBatch?.success) {
        const message =
          result.data?.executePowerSyncBatch?.message || 'Unknown error';
        logger.error('PowerSync batch operation failed:', message);
        throw new Error(`Batch operation failed: ${message}`);
      }

      logger.debug(
        `PowerSync batch completed successfully. Affected rows: ${
          result.data.executePowerSyncBatch.affectedRows || 0
        }`,
      );
    }

    // Completes the transaction and moves onto the next one
    await transaction.complete();
  }
}
